import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import Pusher from 'pusher-js';
import api from '../api';
import './shared-view-page.scss';

interface SharedView {
  slug: string;
  name: string;
  html: string;
}

interface SharedViewsResponse {
  success: boolean;
  appId: string;
  appName: string;
  views: SharedView[];
}

const SharedViewPage: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [appId, setAppId] = useState<string | null>(null);
  const [appName, setAppName] = useState('');
  const [views, setViews] = useState<SharedView[]>([]);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [iframeReady, setIframeReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiBase = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    if (!shareToken) return;
    setLoading(true);
    setError(null);

    api
      .get<SharedViewsResponse>(`/public/views/${shareToken}`)
      .then((res) => {
        if (res.data?.success && res.data.views.length > 0) {
          setAppId(res.data.appId);
          setAppName(res.data.appName);
          setViews(res.data.views);
          setActiveSlug(res.data.views[0].slug);
        } else {
          setError('No views found');
        }
      })
      .catch((err) => {
        console.error('Error fetching shared views:', err);
        setError(
          err?.response?.status === 404
            ? 'Shared page not found'
            : 'Failed to load',
        );
      })
      .finally(() => setLoading(false));
  }, [shareToken]);

  // Subscribe to realtime state updates via Pusher (public channel, no auth)
  useEffect(() => {
    if (!appId) return;
    const pusherKey = process.env.REACT_APP_PUSHER_KEY;
    const pusherCluster = process.env.REACT_APP_PUSHER_CLUSTER;
    const pusherEnv = process.env.REACT_APP_PUSHER_ENV;
    if (!pusherKey || !pusherCluster || !pusherEnv) return;

    const pusher = new Pusher(pusherKey, {
      cluster: pusherCluster,
      forceTLS: true,
    });

    const channelName = `${pusherEnv}-project-app-state-${appId}`;
    const channel = pusher.subscribe(channelName);

    channel.bind('state-updated', () => {
      // Tell the iframe to re-fetch state
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          { type: 'dream-state-refetch' },
          '*',
        );
      }
    });

    return () => {
      channel.unbind_all();
      pusher.unsubscribe(channelName);
      pusher.disconnect();
    };
  }, [appId]);

  const activeView = useMemo(
    () => views.find((v) => v.slug === activeSlug) || null,
    [views, activeSlug],
  );

  // Build the bridge script for the iframe — uses share token for public state access
  const wrappedHtml = useMemo(() => {
    if (!activeView?.html) return '';
    const publicStateUrl = `${apiBase}/public/views/${shareToken}/state`;
    const bridge = `
<script>
(function() {
  var PUBLIC_STATE_URL = ${JSON.stringify(publicStateUrl)};

  window.__DREAM_API_BASE__ = ${JSON.stringify(apiBase)};
  window.__DREAM_APP_ID__ = 'shared';
  window.__DREAM_VIEW_SLUG__ = ${JSON.stringify(activeView.slug)};
  window.__DREAM_SHARE_TOKEN__ = ${JSON.stringify(shareToken)};
  window.__DREAM_AUTH_TOKEN__ = 'shared-public-token';

  // Rewrite fetch calls to the authenticated state endpoint → public endpoint
  var _origFetch = window.fetch;
  window.fetch = function(url, opts) {
    if (typeof url === 'string' && url.indexOf('/project-apps/') !== -1 && url.indexOf('/state') !== -1) {
      url = PUBLIC_STATE_URL;
      // Strip the Authorization header — public endpoint doesn't need it
      opts = opts || {};
      if (opts.headers) {
        if (opts.headers instanceof Headers) {
          opts.headers.delete('Authorization');
          opts.headers.delete('authorization');
        } else if (typeof opts.headers === 'object') {
          var h = Object.assign({}, opts.headers);
          delete h['Authorization'];
          delete h['authorization'];
          opts = Object.assign({}, opts, { headers: h });
        }
      }
    }
    return _origFetch.call(this, url, opts);
  };

  // dream-ready is sticky: listeners added AFTER it fires still get invoked.
  // This eliminates the race where the PVA-generated HTML registers its
  // dream-ready listener async (after a fetch, in a load handler, etc.)
  // and would otherwise miss the event. We can't control the generated
  // HTML's listener-registration timing, so we make the event tolerant
  // of late subscribers.
  var _dreamReadyFired = false;
  function fireDreamReady() {
    if (_dreamReadyFired) return;
    _dreamReadyFired = true;
    window.dispatchEvent(new Event('dream-ready'));
  }

  var _origAddEventListener = window.addEventListener.bind(window);
  window.addEventListener = function(type, listener, options) {
    if (type === 'dream-ready' && _dreamReadyFired && typeof listener === 'function') {
      // Already fired — invoke async to preserve event semantics.
      setTimeout(function() {
        try {
          if (typeof listener === 'function') listener.call(window, new Event('dream-ready'));
          else if (listener && typeof listener.handleEvent === 'function') listener.handleEvent(new Event('dream-ready'));
        } catch (_) {}
      }, 0);
      var once = options && (options === true ? false : options.once);
      if (once) return;
    }
    return _origAddEventListener(type, listener, options);
  };

  // Listen for state refetch signal from parent (realtime Pusher update)
  // and dream-auth (also fires dream-ready as a redundant signal).
  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'dream-state-refetch') {
      _origFetch.call(window, PUBLIC_STATE_URL)
        .then(function(r) { return r.json(); })
        .then(function(json) {
          if (json && json.data) {
            window.dispatchEvent(new CustomEvent('dream-state-updated', { detail: json.data }));
          }
        })
        .catch(function() {});
    }
    if (e.data && e.data.type === 'dream-auth') {
      fireDreamReady();
    }
  });

  function ready() {
    try { parent.postMessage({ type: 'dream-iframe-ready' }, '*'); } catch (_) {}
    // Fire dream-ready after a microtask so listeners registered in the
    // same DOMContentLoaded tick run first. Late subscribers are still
    // covered by the sticky-listener shim above.
    Promise.resolve().then(fireDreamReady);
  }
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    ready();
  } else {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  }
})();
</script>`;

    if (/<head[^>]*>/i.test(activeView.html)) {
      return activeView.html.replace(/<head([^>]*)>/i, `<head$1>${bridge}`);
    }
    return bridge + activeView.html;
  }, [activeView?.html, apiBase, shareToken]);

  // Reset iframe ready state when the HTML changes
  useEffect(() => {
    setIframeReady(false);
  }, [wrappedHtml]);

  // Listen for messages from iframe (e.g. navigate)
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (!iframeRef.current || e.source !== iframeRef.current.contentWindow)
        return;
      // No auth token to send for shared views — just acknowledge readiness
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleTabChange = useCallback((slug: string) => {
    setActiveSlug(slug);
    setIframeReady(false);
  }, []);

  if (loading) {
    return (
      <div className='shared-view-page'>
        <div className='shared-view-page__loading'>
          <div className='loading-spinner' />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='shared-view-page'>
        <div className='shared-view-page__error'>
          <h2>Unable to load shared page</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const showTabs = views.length > 1;
  const showLoader = !iframeReady;

  return (
    <div className='shared-view-page'>
      {showTabs && (
        <header className='shared-view-page__header'>
          <nav className='shared-view-page__tabs'>
            {views.map((v) => (
              <button
                key={v.slug}
                className={`shared-view-page__tab ${
                  v.slug === activeSlug ? 'shared-view-page__tab--active' : ''
                }`}
                onClick={() => handleTabChange(v.slug)}
              >
                {v.name}
              </button>
            ))}
          </nav>
        </header>
      )}

      <div className='shared-view-page__content'>
        {activeView && (
          <iframe
            ref={iframeRef}
            title={activeView.name || 'Shared view'}
            srcDoc={wrappedHtml}
            sandbox='allow-scripts allow-forms allow-popups'
            onLoad={() => setIframeReady(true)}
            className='shared-view-page__iframe'
            style={{ visibility: iframeReady ? 'visible' : 'hidden' }}
          />
        )}
        {showLoader && (
          <div className='shared-view-page__loading-overlay'>
            <div className='loading-spinner' />
          </div>
        )}
      </div>

      <footer className='shared-view-page__footer'>
        <img
          src='/favicon.svg'
          alt='Dream'
          className='shared-view-page__footer-icon'
        />
        This app built by a{' '}
        <a href='https://usedream.ai' target='_blank' rel='noopener noreferrer'>
          Dream Personal Assistant
        </a>
      </footer>
    </div>
  );
};

export default SharedViewPage;
